import { createContext, useState, useEffect, useRef } from "react";
import authService from "@/services/auth/authService";
import profileService from "@/services/setting/profileService";
import { removeData } from '@/utils';
import { createBrowserSafeId } from "@/utils/createBrowserSafeId";
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  let token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
  const tabId = useRef(createBrowserSafeId());

  // Function to verify authentication status
  const verify = async () => {
    try {
      if (!token) {
        token = localStorage.getItem("shared_session_token");
        if (token) sessionStorage.setItem("access_token", token);
      }
      const user = localStorage.getItem("user");

      if (token && user) {
        setAuth({ token, user: JSON.parse(user) });
      } else {
        setAuth(null);
      }
    } catch (error) {
      console.error("Auth verification failed", error);
      setAuth(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verify();

    // Handle storage events for shared tab session
    const handleStorage = (e) => {
      if (e.key === 'shared_session_token' && e.newValue && !sessionStorage.getItem("access_token")) {
        sessionStorage.setItem("access_token", e.newValue);
        verify();
      }

      if ((e.key === 'access_token' || e.key === 'shared_session_token') && e.newValue === null) {
        sessionStorage.removeItem("access_token");
        setAuth(null);
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Sync open tabs for shared tab session
  useEffect(() => {
    const openTabs = JSON.parse(localStorage.getItem("open_tabs") || "[]");
    if (!openTabs.includes(tabId.current)) {
      localStorage.setItem("open_tabs", JSON.stringify([...openTabs, tabId.current]));
    }

    const handleUnload = () => {
      let tabs = JSON.parse(localStorage.getItem("open_tabs") || "[]");
      tabs = tabs.filter(id => id !== tabId.current);

      if (tabs.length === 0) {
        localStorage.removeItem("shared_session_token");
      }

      localStorage.setItem("open_tabs", JSON.stringify(tabs));
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  // The login pages set "sa_login" so the auth flow (login → 2FA → logout) targets
  // the super-admin endpoints. This survives the pre-auth steps where no user is
  // stored yet, and is cleared on logout / regular login.
  const shouldUseSuperAdminAuth = () => {
    try {
      return localStorage.getItem("sa_login") === "1";
    } catch {
      return false;
    }
  };

  const login = async (credentials, remember_me) => {
    removeData('logoutTriggered')
    removeData('forceLogout')
    removeData('last_active')
    try {
      const isSuperAdmin = shouldUseSuperAdminAuth();
      // A device token saved by a previous "trust this device" choice lets the
      // server skip the OTP challenge (regular users only).
      const trustedDeviceToken = isSuperAdmin ? null : localStorage.getItem("trusted_device_token");
      const response = isSuperAdmin
        ? await authService.superAdminlogin(credentials)
        : await authService.login(
            trustedDeviceToken ? { ...credentials, trusted_device_token: trustedDeviceToken } : credentials
          );
      // console.log('response',response)
      if (response.requires_2fa) {
        if (trustedDeviceToken) {
          // The server still wants 2FA, so the token is expired or revoked.
          localStorage.removeItem("trusted_device_token");
        }
        localStorage.setItem("2fa_token", response.token);
        return;
      } else {
        if (response.data.user.referral_attorney) {
          localStorage.setItem("referral_attorney", response.data.user.referral_attorney);
        }

        if (remember_me) {
          localStorage.setItem("access_token", response.data.access_token);
        } else {
          sessionStorage.setItem("access_token", response.data.access_token);
          localStorage.setItem("shared_session_token", response.data.access_token);
        }
        localStorage.setItem("user", JSON.stringify(response.data.user))
        const newAuth = { token: response.data.access_token, user: response.data.user };
        setAuth(newAuth);
      }
    } catch (error) {
      throw new Error(error.customMessage);
    }
  };

  const verify2faLoginOTP = async (credentials) => {
    try {
      const response = shouldUseSuperAdminAuth() 
        ? await authService.superAdminVerify2faLoginOTP(credentials) 
        : await authService.verify2faLoginOTP(credentials);
      const remember_me = localStorage.getItem("remember_me");
      console.log('reme', typeof (remember_me))
      if (remember_me === 'false') {
        sessionStorage.setItem("access_token", response.data.access_token);
        localStorage.setItem("shared_session_token", response.data.access_token);
      } else {
        localStorage.setItem("access_token", response.data.access_token);
      }
      localStorage.setItem("user", JSON.stringify(response.data.user))
      if (response.data.trusted_device_token) {
        localStorage.setItem("trusted_device_token", response.data.trusted_device_token);
      }
      localStorage.removeItem("2fa_token")
      localStorage.removeItem("remember_me")
      const newAuth = { token: response.data.access_token, user: response.data.user };
      setAuth(newAuth);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    setLogoutLoading(true)
    try {
      if (shouldUseSuperAdminAuth()) {
        await authService.superAdminlogout();
      } else {
        await authService.logout();
      }
      sessionStorage.removeItem("access_token");
      removeData("access_token")
      removeData("shared_session_token")
      removeData("user")
      removeData("sa_login")
      setAuth(null);

      removeData("referral_attorney");
    } catch (error) {
      throw error
    } finally {
      setLogoutLoading(false)
    }
  };

  // Function to update (full or partial) user in the auth context
  const updateUser = (newUserData, isPartial = false) => {
    setAuth((prev) => {
      const updatedUser = isPartial
        ? { ...prev.user, ...newUserData }
        : newUserData;

      const updatedAuth = { ...prev, user: updatedUser };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedAuth;
    });
  };

  return (
    <AuthContext.Provider value={{
      auth,
      login,
      logout,
      loading,
      setLoading,
      verify,
      verify2faLoginOTP,
      updateUser,
      logoutLoading,
      setLogoutLoading
    }}>

      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
